<script setup lang="ts">
import { ChevronDown, X, Check, Search } from '@lucide/vue'
import { onClickOutside } from '@vueuse/core'
import { ref, computed, nextTick, watch } from 'vue'

export interface SelectOption {
  id: string | number
  name: string
  group?: string
}

export interface BaseSelectProps {
  modelValue?: string | number | (string | number)[]
  options: SelectOption[]
  multiple?: boolean
  placeholder?: string
  label?: string
  disabled?: boolean
  searchable?: boolean
  minimal?: boolean
  minimalTextClass?: string
}

const props = withDefaults(defineProps<BaseSelectProps>(), {
  modelValue: undefined,
  multiple: false,
  placeholder: 'Select...',
  label: '',
  disabled: false,
  searchable: false,
  minimal: false,
  minimalTextClass: 'text-gray-600',
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | number | (string | number)[] | undefined): void
}>()

const isOpen = ref(false)
const containerRef = ref<HTMLElement | null>(null)
const searchInputRef = ref<HTMLInputElement | null>(null)
const searchQuery = ref('')
const highlightedIndex = ref(-1)
const optionsContainerRef = ref<HTMLElement | null>(null)

onClickOutside(containerRef, () => {
  isOpen.value = false
})

const toggleDropdown = async () => {
  if (props.disabled) return
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    if (props.searchable) {
      searchQuery.value = ''
      await nextTick()
      searchInputRef.value?.focus()
    }
    // Reset highlight
    highlightedIndex.value = -1
  }
}

const isSelected = (optionId: string | number) => {
  if (props.multiple) {
    return Array.isArray(props.modelValue) && props.modelValue.includes(optionId)
  }
  return props.modelValue === optionId
}

const handleSelect = (option: SelectOption) => {
  if (props.multiple) {
    const current = Array.isArray(props.modelValue) ? [...props.modelValue] : []
    const index = current.indexOf(option.id)
    if (index === -1) {
      current.push(option.id)
    } else {
      current.splice(index, 1)
    }
    emit('update:modelValue', current)
  } else {
    emit('update:modelValue', option.id)
    isOpen.value = false
  }
}

const removeSelection = (e: Event, id: string | number) => {
  e.stopPropagation()
  if (props.multiple && Array.isArray(props.modelValue)) {
    const newValue = props.modelValue.filter((v) => v !== id)
    emit('update:modelValue', newValue)
  } else {
    emit('update:modelValue', undefined)
  }
}

const selectedOptions = computed(() => {
  if (props.multiple) {
    const val = props.modelValue
    if (!Array.isArray(val)) return []
    return props.options.filter((o) => val.includes(o.id))
  }
  return props.options.find((o) => o.id === props.modelValue)
})

// Grouping logic
const filteredOptions = computed(() => {
  if (!props.searchable || !searchQuery.value) return props.options
  const query = searchQuery.value.toLowerCase()
  return props.options.filter((o) => o.name.toLowerCase().includes(query))
})

const groupedOptions = computed(() => {
  const groups: Record<string, SelectOption[]> = {}
  const flat: SelectOption[] = []

  filteredOptions.value.forEach((option) => {
    if (option.group) {
      if (!groups[option.group]) {
        groups[option.group] = []
      }
      groups[option.group].push(option)
    } else {
      flat.push(option)
    }
  })

  return { groups, flat }
})

const hasGroups = computed(() => Object.keys(groupedOptions.value.groups).length > 0)

const visibleOptions = computed(() => {
  if (hasGroups.value) {
    const options: SelectOption[] = []
    // Groups
    Object.keys(groupedOptions.value.groups).forEach((groupName) => {
      options.push(...groupedOptions.value.groups[groupName])
    })
    // Flat items in grouped mode
    options.push(...groupedOptions.value.flat)
    return options
  }
  return filteredOptions.value
})

const scrollToHighlighted = () => {
  if (!optionsContainerRef.value || highlightedIndex.value === -1) return

  const highlightedOption = visibleOptions.value[highlightedIndex.value]
  if (!highlightedOption) return

  // Find element by ID
  // We need to add IDs to the option elements in the template
  const element = optionsContainerRef.value.querySelector(
    `[data-option-id="${highlightedOption.id}"]`,
  )
  if (element) {
    element.scrollIntoView({ block: 'nearest' })
  }
}

const handleKeyDown = (e: KeyboardEvent) => {
  if (props.disabled) return

  if (!isOpen.value) {
    if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(e.key)) {
      e.preventDefault()
      toggleDropdown()
    }
    return
  }

  switch (e.key) {
    case 'ArrowDown':
      e.preventDefault()
      highlightedIndex.value = (highlightedIndex.value + 1) % visibleOptions.value.length
      scrollToHighlighted()
      break
    case 'ArrowUp':
      e.preventDefault()
      highlightedIndex.value =
        (highlightedIndex.value - 1 + visibleOptions.value.length) % visibleOptions.value.length
      scrollToHighlighted()
      break
    case 'Enter':
      e.preventDefault()
      if (highlightedIndex.value !== -1) {
        handleSelect(visibleOptions.value[highlightedIndex.value])
      } else if (visibleOptions.value.length > 0 && props.searchable && searchQuery.value) {
        // If searching and user hits enter without navigating, select first result
        handleSelect(visibleOptions.value[0])
      }
      break
    case 'Escape':
      e.preventDefault()
      isOpen.value = false
      break
    case 'Tab':
      isOpen.value = false
      break
  }
}

watch(searchQuery, () => {
  highlightedIndex.value = -1
})
</script>

<template>
  <div ref="containerRef" class="relative" :class="minimal ? 'inline-block' : 'w-full'">
    <label v-if="label" class="block text-sm font-medium text-gray-700 mb-1">
      {{ label }}
    </label>

    <!-- Minimal trigger: inline text + micro chevron -->
    <div
      v-if="minimal"
      class="inline-flex items-center gap-0.5 rounded px-1 py-0.5 -mx-1 transition-colors cursor-pointer hover:ring-1 hover:ring-gray-300 focus:outline-none"
      :class="[
        isOpen ? 'ring-1 ring-emerald-500 bg-white' : '',
        disabled ? 'opacity-50 cursor-not-allowed' : '',
      ]"
      @click="toggleDropdown"
      @keydown="handleKeyDown"
      tabindex="0"
    >
      <span class="text-sm" :class="minimalTextClass">
        <template v-if="!multiple && selectedOptions && !Array.isArray(selectedOptions)">{{
          selectedOptions.name
        }}</template>
        <template v-else>{{ placeholder }}</template>
      </span>
      <ChevronDown class="w-3 h-3 text-gray-400 shrink-0" />
    </div>

    <!-- Standard trigger -->
    <div
      v-else
      class="min-h-[38px] w-full bg-white border border-gray-300 rounded-xl shadow-sm px-3 py-1.5 flex items-center justify-between cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
      :class="{ 'bg-gray-50 cursor-not-allowed': disabled }"
      @click="toggleDropdown"
      @keydown="handleKeyDown"
      tabindex="0"
    >
      <div class="flex flex-wrap gap-1.5 flex-1">
        <!-- Multiple Selection Display -->
        <template v-if="multiple && Array.isArray(selectedOptions) && selectedOptions.length > 0">
          <span
            v-for="option in selectedOptions"
            :key="option.id"
            class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800"
          >
            {{ option.name }}
            <button
              type="button"
              class="ml-1 inline-flex items-center justify-center text-primary-400 hover:text-primary-600 focus:outline-none"
              @click="(e) => removeSelection(e, option.id)"
            >
              <X class="h-3 w-3" />
            </button>
          </span>
        </template>

        <!-- Single Selection Display -->
        <template v-else-if="!multiple && selectedOptions && !Array.isArray(selectedOptions)">
          <span class="text-sm text-gray-900">{{ selectedOptions.name }}</span>
        </template>

        <!-- Placeholder -->
        <span v-else class="text-sm text-gray-500">{{ placeholder }}</span>
      </div>

      <div class="flex items-center ml-2 pointer-events-none">
        <ChevronDown class="h-4 w-4 text-gray-400" />
      </div>
    </div>

    <!-- Dropdown Menu -->
    <div
      v-if="isOpen"
      class="absolute z-50 mt-1 bg-white shadow-lg rounded-xl py-1 text-base ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm overflow-hidden"
      :class="minimal ? 'min-w-[200px]' : 'w-full'"
    >
      <!-- Search Input -->
      <div v-if="searchable" class="px-3 py-2 border-b border-gray-100">
        <div class="relative">
          <Search class="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
          <input
            ref="searchInputRef"
            v-model="searchQuery"
            type="text"
            class="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
            placeholder="Search..."
            @click.stop
            @keydown="handleKeyDown"
          />
        </div>
      </div>

      <div ref="optionsContainerRef" class="max-h-60 overflow-auto">
        <!-- Grouped Options -->
        <template v-if="hasGroups">
          <div v-for="(groupOptions, groupName) in groupedOptions.groups" :key="groupName">
            <div
              class="px-3 py-2 text-xs font-semibold text-gray-500 bg-gray-100 uppercase tracking-wider sticky top-0 z-10"
            >
              {{ groupName }}
            </div>
            <div
              v-for="option in groupOptions"
              :key="option.id"
              class="cursor-pointer select-none relative py-2 pl-3 pr-9 text-gray-900"
              :class="{
                'bg-primary-50':
                  highlightedIndex !== -1 && visibleOptions[highlightedIndex]?.id === option.id,
                'hover:bg-primary-50': highlightedIndex === -1,
              }"
              :data-option-id="option.id"
              @click="handleSelect(option)"
            >
              <span
                :class="['block truncate', isSelected(option.id) ? 'font-semibold' : 'font-normal']"
              >
                {{ option.name }}
              </span>
              <span
                v-if="isSelected(option.id)"
                class="absolute inset-y-0 right-0 flex items-center pr-4 text-primary-600"
              >
                <Check class="h-4 w-4" />
              </span>
            </div>
          </div>
          <!-- Flat options that didn't have a group -->
          <div v-if="groupedOptions.flat.length > 0">
            <div
              v-if="Object.keys(groupedOptions.groups).length > 0"
              class="border-t border-gray-100 my-1"
            ></div>
            <div
              v-for="option in groupedOptions.flat"
              :key="option.id"
              class="cursor-pointer select-none relative py-2 pl-3 pr-9 text-gray-900"
              :class="{
                'bg-primary-50':
                  highlightedIndex !== -1 && visibleOptions[highlightedIndex]?.id === option.id,
                'hover:bg-primary-50': highlightedIndex === -1,
              }"
              :data-option-id="option.id"
              @click="handleSelect(option)"
            >
              <span
                :class="['block truncate', isSelected(option.id) ? 'font-semibold' : 'font-normal']"
              >
                {{ option.name }}
              </span>
              <span
                v-if="isSelected(option.id)"
                class="absolute inset-y-0 right-0 flex items-center pr-4 text-primary-600"
              >
                <Check class="h-4 w-4" />
              </span>
            </div>
          </div>
        </template>

        <!-- Flat List (No Groups) -->
        <template v-else>
          <div
            v-for="option in filteredOptions"
            :key="option.id"
            class="cursor-pointer select-none relative py-2 pl-3 pr-9 text-gray-900"
            :class="{
              'bg-primary-50':
                highlightedIndex !== -1 && visibleOptions[highlightedIndex]?.id === option.id,
              'hover:bg-primary-50': highlightedIndex === -1,
            }"
            :data-option-id="option.id"
            @click="handleSelect(option)"
          >
            <span
              :class="['block truncate', isSelected(option.id) ? 'font-semibold' : 'font-normal']"
            >
              {{ option.name }}
            </span>
            <span
              v-if="isSelected(option.id)"
              class="absolute inset-y-0 right-0 flex items-center pr-4 text-primary-600"
            >
              <Check class="h-4 w-4" />
            </span>
          </div>
        </template>

        <div v-if="filteredOptions.length === 0" class="py-2 px-3 text-sm text-gray-500">
          No options available
        </div>
      </div>
    </div>
  </div>
</template>
